import cx from 'classnames';
import { useIntl } from 'react-intl';

import { ampli } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import { TrackingProperties } from '../../../core/Tracking/Amplitude/Amplitude';
import { OptionWayTransportCard } from '../../../design-system/components/card/transport/OptionWayTransportCard';
import { RailEuropeTransportCard } from '../../../design-system/components/card/transport/RailEuropeTransportCard';
import { Scale, Typography } from '../../../design-system/components/typography/Typography';
import { Carousel } from '../../../domains/Layout/Carousel/Carousel';
import { HtmlContent } from '../../../domains/Layout/HtmlContent/HtmlContent';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { DestinationContents } from '../../../resources/destinationContents/domain/types/destinationContents';
import { useCurrentSite } from '../../../shared/providers/currentSite/useCurrentSite';
import { IntrinsicElement } from '../../../shared/utilities/types/IntrinsicElement';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

import style from './TransportSection.module.css';

type TransportSectionProps = {
    destination: PickDomain<Destination, 'name' | 'preposition' | 'id' | 'slug'>;
    showRailEurope: boolean;
    showOptionWay: boolean;
    trackingInformation: {
        transportSearchClicked: Pick<
            TrackingProperties<typeof ampli.transportSearchClicked>,
            'block'
        >;
    };
    howToGoContent?: DestinationContents['howToGoContent'] | null;
    titleElement?: IntrinsicElement;
    titleScale?: Scale;
    locationName?: string;
    locationPreposition?: string;
    descriptionFullWidth?: boolean;
    compactCards?: boolean;
};

export function TransportSection({
    destination,
    showRailEurope,
    showOptionWay,
    trackingInformation,
    howToGoContent,
    titleElement = 'h2',
    titleScale = 'primary-l-extra-bold',
    locationName = destination.name,
    locationPreposition = destination.preposition,
    descriptionFullWidth = false,
    compactCards = false,
}: TransportSectionProps) {
    const { currentSite } = useCurrentSite();
    const { formatMessage } = useIntl();

    const descriptionColumnClass = descriptionFullWidth ? 'col-12' : 'col-12 col-lg-6';

    return (
        <div>
            <Typography as={titleElement} scale={titleScale} className={style.title}>
                {formatMessage(
                    { id: 'global.component.transports.title' },
                    {
                        destinationName: locationName,
                        destinationPreposition: locationPreposition,
                    },
                )}
            </Typography>

            {howToGoContent ? (
                <div className={cx('row', style.descriptionRow)}>
                    <div className={descriptionColumnClass}>
                        <HtmlContent htmlContent={howToGoContent} />
                    </div>
                </div>
            ) : (
                <div className={cx('row', style.descriptionRow)}>
                    <div className={descriptionColumnClass}>
                        <Typography
                            as="p"
                            scale="primary-body-regular"
                            className={style.subtitle}
                            dangerouslySetInnerHTML={{
                                __html: formatMessage(
                                    { id: 'global.component.transports.description' },
                                    {
                                        destinationName: locationName,
                                        destinationPreposition: locationPreposition,
                                    },
                                ),
                            }}
                        />
                    </div>
                </div>
            )}

            <Carousel
                mobilePeek="narrow"
                slidesVisibleRules={{
                    sm: 2,
                    md: 2,
                    lg: 2,
                    xl: 2,
                    xxl: 2,
                }}
            >
                {showRailEurope && currentSite.railEuropeUrl ? (
                    <RailEuropeTransportCard
                        destination={destination}
                        trackingInformation={trackingInformation}
                        railEuropeUrl={currentSite.railEuropeUrl}
                        isCompact={compactCards}
                        className={style.card}
                    />
                ) : null}

                {showOptionWay && currentSite.optionWayUrl ? (
                    <OptionWayTransportCard
                        destination={destination}
                        trackingInformation={trackingInformation}
                        optionWayUrl={currentSite.optionWayUrl}
                        isCompact={compactCards}
                        className={style.card}
                    />
                ) : null}
            </Carousel>
        </div>
    );
}
